import React, { useMemo, useState } from 'react';
import { XCircle } from 'lucide-react';
import { Avatar } from './media';

/*
 * Pick creators by searching for them.
 *
 * The add and edit video forms rendered every creator as a toggle pill. That
 * was fine at a dozen and is unusable at 144: a wall of identical chips with
 * no order to it, where finding one person means reading all of them. It also
 * got worse every time a creator was added, which is the wrong direction for a
 * form used precisely when adding creators.
 *
 * Selected people stay visible as chips, because the question being answered
 * is "who is in this video" and the answer needs to be readable at a glance.
 * Everyone else is behind a search box.
 */
export default function CreatorPicker({
  authors,
  selectedIds,
  onChange,
  placeholder = 'Search creators...',
  limit = 8,
  emptyHint = null,
}) {
  const [query, setQuery] = useState('');

  const selected = useMemo(
    () => selectedIds.map((id) => authors.find((a) => a.id === id)).filter(Boolean),
    [authors, selectedIds]
  );

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = authors.filter((a) => !selectedIds.includes(a.id));
    if (!q) {
      // With nothing typed, offer the people most likely to be wanted rather
      // than the first eight alphabetically, which is noise.
      return pool.slice(0, limit);
    }
    return pool
      .filter((a) => a.name.toLowerCase().includes(q))
      .sort((a, b) => {
        // A name starting with what was typed beats one merely containing it.
        const aStarts = a.name.toLowerCase().startsWith(q);
        const bStarts = b.name.toLowerCase().startsWith(q);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
        return a.name.localeCompare(b.name);
      })
      .slice(0, limit);
  }, [authors, selectedIds, query, limit]);

  const add = (id) => {
    onChange([...selectedIds, id]);
    setQuery('');
  };
  const remove = (id) => onChange(selectedIds.filter((x) => x !== id));

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <span
              key={c.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 text-blue-900 pl-1 pr-2 py-0.5 text-sm"
            >
              <Avatar src={c.photo} name={c.name} size={20} />
              {c.name}
              <button
                type="button"
                onClick={() => remove(c.id)}
                className="text-blue-600 hover:text-blue-900"
                aria-label={`Remove ${c.name}`}
              >
                <XCircle className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          // Enter takes the top match, so a name can be added without reaching
          // for the mouse.
          if (e.key === 'Enter' && suggestions.length) {
            e.preventDefault();
            add(suggestions[0].id);
          }
        }}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
      />

      {suggestions.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => add(c.id)}
              className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-slate-200 hover:ring-blue-400 hover:bg-blue-50 pl-1 pr-2.5 py-0.5 text-sm transition"
            >
              <Avatar src={c.photo} name={c.name} size={20} />
              {c.name}
            </button>
          ))}
        </div>
      ) : (
        query.trim() && (
          <p className="text-sm text-slate-500">
            No creator matching "{query.trim()}".{emptyHint ? ` ${emptyHint}` : ''}
          </p>
        )
      )}
    </div>
  );
}
